import { useCallback } from 'react';
import { UploadConfirmDialog } from '@/components/enforcement/UploadConfirmDialog';
import { UploadProgressDialog } from '@/components/products/UploadProgressDialog';
import { UploadSuccessDialog } from '@/components/enforcement/UploadSuccessDialog';
import { useFeedExperiment } from '@/hooks/useFeedExperiment';
import { isExperimentAtRisk } from '@/lib/experimentFork';
import { trackConversion } from '@/lib/analytics';
import type { useUploadJob } from '@/hooks/useUploadJob';

/**
 * Die drei Upload-Dialoge (Bestaetigen / Fortschritt / Erfolg) samt der
 * Verdrahtung dazwischen (Confirm -> handleConfirm -> upload.handleUpload,
 * Experiment-Gabel-Hinweis, Herkunfts-Gate des Fortschrittsdialogs).
 *
 * Extrahiert aus `WorkbenchUploadAction.tsx` (F-B): Task 3 haengt einen
 * ZWEITEN Mount-Ort auf der Feed-Check-Seite ein, dessen "Zu Google
 * uebertragen"-CTA den Bestaetigungsdialog direkt oeffnen soll statt zur
 * Werkbank zu navigieren. Eine Kopie der Dialog-Verdrahtung waere ein
 * Drift-Risiko ersten Ranges — Race-Verhalten wie das Herkunfts-Gate
 * (Konvergenz 4, Befund 2) oder die N7-Zaehlregel (erst nach Server-Annahme
 * melden) muessten an zwei Stellen gepflegt werden und liefen garantiert
 * auseinander. Diese Komponente ist die einzige Quelle, beide Mount-Orte
 * generalisieren nur ueber Props (`approvedCount`, `analyticsSource`,
 * `beforeUpload`).
 *
 * Verhaltensgleich zur Werkbank: reine 1:1-Verschiebung, keine neue Logik.
 */
export interface UploadFlowDialogsProps {
  feedId: string;
  upload: ReturnType<typeof useUploadJob>;
  /** Zahl im Confirm-Dialog = das, was der Server mit approved_only waehlt (stats.approved). */
  approvedCount: number;
  /** trackConversion('upload_started', { count, source }) */
  analyticsSource: 'workbench' | 'feed_check';
  /** Werkbank: flushPendingEdits. false ⇒ Upload wird NICHT gestartet. */
  beforeUpload?: () => Promise<boolean>;
}

export function UploadFlowDialogs({
  feedId,
  upload,
  approvedCount,
  analyticsSource,
  beforeUpload,
}: UploadFlowDialogsProps) {
  // Erst beim Oeffnen des Dialogs: `experiment_eligibility` rechnet die
  // Nachweisgrenze inline ueber die ganze Population (gemessen ~164 ms auf
  // 15.668 Produkten). Das gehoert nicht in jeden Seitenaufruf (Werkbank ODER
  // Feed-Check), sondern genau in den Moment, in dem die Gabel ansteht. `null`
  // als feedId haelt die Abfrage aus.
  const { eligibility } = useFeedExperiment(upload.showUploadConfirmDialog ? feedId : null);

  const handleConfirm = useCallback(
    async (addCustomLabel: boolean) => {
      // Offene Handkorrektur zuerst wegschreiben — sonst geht der alte Text zu
      // Google, waehrend die Karte den neuen als das ausweist, was hochgeladen
      // wuerde. Schlaegt das Speichern fehl, wird gar nicht hochgeladen: die
      // Meldung dazu kommt aus `saveChanges` (bzw. dem jeweiligen
      // `beforeUpload`), und ein Upload des alten Textes waere hier die
      // teuerste aller Antworten (`needs_reupload` gibt es bewusst nicht).
      if (beforeUpload && (await beforeUpload()) === false) return;

      // `approved_only: true` ist seit Task 11 die einzige Moeglichkeit, die der
      // Dialog noch anbietet — und die einzige, die der Server fuer einen
      // Browser-Aufruf ueberhaupt noch bedient.
      const enqueued = await upload.handleUpload(true, addCustomLabel);
      // N7: erst nach der Annahme durch den Server melden, und mit der Zahl,
      // die wirklich eingereiht wurde. Gleiches Muster wie die Freigabe-
      // Entscheidung in useWorkbench.ts.
      if (enqueued !== null) {
        trackConversion('upload_started', { count: enqueued, source: analyticsSource });
      }
    },
    [upload, beforeUpload, analyticsSource],
  );

  const activeJob = upload.completedUploadJob ?? upload.activeUploadJob;
  // Konvergenz 4, Befund 2: ein Lauf, den NICHT diese Instanz gestartet hat
  // (zweiter Tab, naechtlicher Auto-Upload) darf kein fokusfangendes Modal
  // aufziehen — er wird als unaufdringliche Fortschrittszeile gezeigt (die
  // bleibt beim jeweiligen Mount-Ort, nicht Teil dieser Komponente).

  return (
    <>
      {/* Die Dialoge haengen bewusst NICHT an der Sichtbarkeit des Upload-
          Knopfes: sobald der Upload durch ist, faellt die wartende Menge auf
          null — der Fortschritts- und der Erfolgsdialog wuerden mitten im
          Lauf verschwinden. */}
      <UploadConfirmDialog
        open={upload.showUploadConfirmDialog}
        onClose={() => upload.setShowUploadConfirmDialog(false)}
        onConfirm={handleConfirm}
        approvedCount={approvedCount}
        experimentAtRisk={isExperimentAtRisk(eligibility)}
        submitting={upload.uploadSubmitting}
      />
      <UploadProgressDialog
        // `uploadDialogDismissed`: X/Escape waehrend des Laufs blenden den
        // Dialog nur aus (der Job laeuft weiter, genau wie die Copy sagt);
        // bei Completion mit Ergebnis setzt useUploadJob das Flag zurueck.
        // `uploadStartedHere` (Befund 2): nur ein in DIESER Instanz
        // gestarteter Lauf zieht das Modal auf — fremde Tabs und Auto-Uploads
        // bekommen die Fortschrittszeile oben statt einer Fokusfalle mit
        // destruktivem Abbrechen-Knopf.
        open={(((upload.isUploading && !!upload.activeUploadJob) || !!upload.completedUploadJob) && !upload.uploadDialogDismissed && upload.uploadStartedHere)}
        totalProducts={activeJob?.total_products || 0}
        uploadedProducts={activeJob?.uploaded_products || 0}
        failedProducts={activeJob?.failed_products || 0}
        queuedBatches={activeJob?.queued_batches}
        processedBatches={activeJob?.processed_batches}
        status={activeJob?.status}
        // Der Fehlergrund aus dem Completion-Nachfetch (upload_jobs.error_message):
        // ohne ihn zeigte der failed-Endzustand Zahlen ohne Ursache.
        errorMessage={activeJob?.error_message ?? null}
        onCancel={upload.handleCancelUpload}
        onClose={upload.handleCloseUploadDialog}
        onDismiss={upload.dismissUploadDialog}
        cancelling={upload.cancellingUpload}
      />
      <UploadSuccessDialog
        open={upload.showUploadSuccessDialog}
        onClose={() => upload.setShowUploadSuccessDialog(false)}
        uploadedCount={upload.lastUploadStats.uploaded}
        failedCount={upload.lastUploadStats.failed}
      />
    </>
  );
}
